// tslint:disable-next-line: no-submodule-imports
import parseISO from 'date-fns/parseISO';
import { action, autorun, computed, makeObservable, observable, when } from 'mobx';
import { EmbedStore } from '../../store/embed-store';
import { ErrorCode } from '../errors/error-codes';
import { logger } from '../logging.service';
import { storageService } from '../storage/storage.service';
import { AuthResponse, authSvc } from './auth.service';
import jwt_decode from 'jwt-decode';
import { PageType } from '../../admin/vacs-admin-manual-vehicles/vacs-admin-manual-vehicle.model';

const FIVE_MINUTES_MS = 5 * 60 * 1000;
const AUTH_STORAGE_KEY = 'auth_';

export interface AuthInfo {
  apiUrl: string;
  additionalQueryParams: { key: string; value: any }[] | undefined;
  headers: Headers;
  setting: string | undefined;
  moduleId: string;
  moduleVariantId: string;
  adminMode?: boolean | undefined;
  group_id?: string | number;
  shiroSessionId?: string | undefined;
  amAuthUserSessionId?: string | undefined;
  pageType?: PageType;
  useVacs?: boolean;
  isCmsAvailable?: boolean;
}

export interface ArticleUtmParams {
  utm_content?: string | null;
  utm_source?: string | null;
  utm_term?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
}

type AuthState = 'LOGGED_OUT' | 'PENDING' | 'LOGGED_IN' | 'LOGIN_FAILED';

export type AuthSupplier = () => Promise<AuthInfo>;

export const noAuth: AuthSupplier = () => Promise.reject('No Auth is called');

export class AuthStore {
  public jwtToken: string | undefined;
  public tokenExpiresAt: string | undefined;
  private authState: AuthState = 'LOGGED_OUT';
  private activeApiKey: string | undefined;

  constructor(private readonly embedStore: EmbedStore) {
    makeObservable<AuthStore, 'authState' | 'activeApiKey' | 'checkLogin' | 'login' | 'setLoginState'>(this, {
      loggedIn: computed,
      jwtToken: observable,
      tokenExpiresAt: observable,
      authState: observable,
      activeApiKey: observable,
      checkLogin: action,
      login: action,
      setLoginState: action,
    });

    autorun(() => {
      const apiKey = this.embedStore.apiKey;
      if (apiKey !== this.activeApiKey || this.embedStore.customEnvironment) {
        logger.log('ApiKey changed, checking login');
        this.checkLogin();
      }
    });
  }

  get loggedIn() {
    return this.authState === 'LOGGED_IN';
  }

  public getAuthInfo = () => {
    this.checkLogin();
    return new Promise<AuthInfo>((resolve) =>
      when(
        () => this.loggedIn,
        () =>
          resolve({
            apiUrl: this.embedStore.apiUrl || '',
            additionalQueryParams: this.embedStore?.additionalQueryParams,
            headers: new Headers({
              ...this.embedStore.customHeaders,
              Authorization: 'Bearer ' + this.jwtToken,
            }),
            setting: this.embedStore.setting,
            moduleId: this.embedStore.moduleId,
            moduleVariantId: this.embedStore.moduleVariantId,
            adminMode: this.embedStore.adminMode,
            pageType: this.embedStore.pageType,
            useVacs: this.embedStore.customData?.useVacs,
            isCmsAvailable: this.embedStore.customData?.isCmsAvailable,
          })
      )
    );
  };

  private checkLogin() {
    if (this.authState === 'PENDING' || this.embedStore.apiUrl === '') {
      return;
    }

    const apiKey = this.embedStore.apiKey;
    if (apiKey !== this.activeApiKey) {
      this.setLoginState('LOGGED_OUT', {});
      this.activeApiKey = apiKey;
    }

    if (!this.jwtToken && apiKey) {
      const authStored = storageService.getItem<AuthResponse>(this.authStorageKey(apiKey));
      if (authStored && !this.isTokenNearExpiration(authStored.expiresAt)) {
        logger.log('Using stored authInfo');
        this.setLoginState('LOGGED_IN', authStored);
        const decodedJwtToken = jwt_decode(authStored.token);
        this.embedStore.decodedJwtToken = decodedJwtToken;
        this.embedStore.permissions = (decodedJwtToken as any).permissions;
        this.embedStore.setIsCountrySite((decodedJwtToken as any).isCountrySite || false);
      }
    }

    if (!this.jwtToken || this.isTokenNearExpiration(this.tokenExpiresAt)) {
      this.jwtToken ? logger.log('Auth needed') : logger.log('Auth token expired.');
      this.login();
    }

    if (
      this.embedStore.adminMode &&
      this.embedStore.customEnvironment !== 'local' &&
      ((this.embedStore.decodedJwtToken as any)?.shiroSessionId !== this.embedStore?.shiroSessionId ||
        (this.embedStore.decodedJwtToken as any)?.amAuthUserSessionId !== this.embedStore?.amAuthUserSessionId)
    ) {
      logger.log('Case of adminMode, login is needed');
      this.login();
    }
  }

  private async login() {
    this.authState = 'PENDING';
    const apiKey = this.embedStore.apiKey;
    try {
      const authInfo = await authSvc.acquireJWTToken(
        apiKey!,
        this.embedStore.loginUrl,
        this.embedStore.shiroSessionId,
        this.embedStore.amAuthUserSessionId
      );
      if (authInfo.message) {
        logger.log('Token expired!');
      } else {
        storageService.clear();
        storageService.setItem(this.authStorageKey(apiKey!), authInfo);
        this.setLoginState('LOGGED_IN', authInfo);
        const decodedJwtToken = jwt_decode(authInfo.token);
        this.embedStore.decodedJwtToken = decodedJwtToken;
        this.embedStore.permissions = (decodedJwtToken as any).permissions;
        this.embedStore.setIsCountrySite((decodedJwtToken as any).isCountrySite || false);
        logger.log('Auth success');
      }
    } catch (error) {
      this.setLoginState('LOGIN_FAILED', {});
      logger.error(ErrorCode.AuthFailed, 'Auth failed', apiKey, error);
    }
  }

  private setLoginState(authState: AuthState, authInfo: Partial<AuthResponse>) {
    this.authState = authState;
    this.jwtToken = authInfo.token;
    this.tokenExpiresAt = authInfo.expiresAt;
  }

  private isTokenNearExpiration(expiration: string | undefined) {
    if (!expiration) {
      return true;
    }
    const expireTimeStamp = parseISO(expiration).getTime();

    if (Number.isNaN(expireTimeStamp)) {
      return true;
    }

    return expireTimeStamp < Date.now() + FIVE_MINUTES_MS;
  }

  private authStorageKey(apiKey: string) {
    return (
      AUTH_STORAGE_KEY + apiKey + ':' + this.embedStore.customEnvironment + (this.embedStore.adminMode ? ':admin' : '')
    );
  }
}
